import { hasPermission } from "@/lib/permissions";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Check,
  Clock3,
  CreditCard,
  Crown,
  Download,
  Loader2,
  LockKeyhole,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Store,
} from "lucide-react";
import { toast } from "sonner";

import { PageContainer } from "@/components/layout/PageContainer";
import { BillingPeriodSelector } from "@/components/subscription/BillingPeriodSelector";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocalization } from "@/lib/localization";
import { checkoutCopy } from "@/lib/commerceLocalization";
import { billingCycleInterval, billingCyclePrice } from "@/lib/billingCycles";
import { useAuth } from "@/lib/store";
import { normalizeDollarSymbol } from "@/lib/money";
import {
  chooseSubscriptionPlanId,
  readSessionStorageValue,
  removeSessionStorageValue,
  resolveSubscriptionPaymentReference,
  writeSessionStorageValue,
} from "@/lib/subscriptionFlow";
import {
  confirmSubscriptionCheckout,
  fetchBillingWallet,
  fetchSubscriptionPaymentReceipt,
  fetchSubscriptionStatus,
  initializeSubscriptionCheckout,
  recordSubscriptionCheckoutClientState,
} from "@/lib/shopApi";

const PENDING_REFERENCE_KEY = "shopbook.subscription.pending-reference";
const PENDING_URL_KEY = "shopbook.subscription.pending-url";

const SUBSCRIPTION_COPY = {
  ar: { billing: 'نظرة عامة على الفوترة', billingHint: 'إعدادات الفوترة الحالية.', choosePlan: 'اختر خطتك', close: 'إغلاق', current: 'الحالية', currentAccess: 'الوصول الحالي', issued: 'تاريخ الإصدار', print: 'طباعة', protected: 'وصول محمي', protectedHint: 'فوترة بسيطة مع ضمانات واضحة.', receipt: 'رقم الإيصال', selected: 'محدد', total: 'الإجمالي' },
  en: { billing: 'Billing overview', billingHint: 'Your current billing configuration.', choosePlan: 'Choose your plan', close: 'Close', current: 'Current', currentAccess: 'Current access', issued: 'Issued', print: 'Print', protected: 'Protected access', protectedHint: 'Simple billing with clear safeguards.', receipt: 'Receipt number', selected: 'Selected', total: 'Total' },
  fr: { billing: 'Aperçu de la facturation', billingHint: 'Votre configuration de facturation actuelle.', choosePlan: 'Choisissez votre forfait', close: 'Fermer', current: 'Actuel', currentAccess: 'Accès actuel', issued: 'Émis le', print: 'Imprimer', protected: 'Accès protégé', protectedHint: 'Une facturation simple avec des garanties claires.', receipt: 'Numéro du reçu', selected: 'Sélectionné', total: 'Total' },
  ha: { billing: 'Bayani kan biyan kuɗi', billingHint: 'Saitin biyan kuɗinka na yanzu.', choosePlan: 'Zaɓi shirinka', close: 'Rufe', current: 'Na yanzu', currentAccess: 'Damar amfani yanzu', issued: 'An bayar', print: 'Buga', protected: 'Dama mai kariya', protectedHint: 'Biya mai sauƙi tare da kariya bayyananniya.', receipt: 'Lambar rasit', selected: 'An zaɓa', total: 'Jimilla' },
  ig: { billing: 'Nchịkọta ịkwụ ụgwọ', billingHint: 'Ntọala ịkwụ ụgwọ gị ugbu a.', choosePlan: 'Họrọ atụmatụ gị', close: 'Mechie', current: 'Ugbu a', currentAccess: 'Nnweta ugbu a', issued: 'E nyere', print: 'Bipụta', protected: 'Nnweta echekwara', protectedHint: 'Ịkwụ ụgwọ dị mfe nwere nchedo doro anya.', receipt: 'Nọmba nnata', selected: 'Ahọpụtara', total: 'Ngụkọta' },
  pt: { billing: 'Visão geral da faturação', billingHint: 'A sua configuração de faturação atual.', choosePlan: 'Escolha o seu plano', close: 'Fechar', current: 'Atual', currentAccess: 'Acesso atual', issued: 'Emitido', print: 'Imprimir', protected: 'Acesso protegido', protectedHint: 'Faturação simples com proteções claras.', receipt: 'Número do recibo', selected: 'Selecionado', total: 'Total' },
  sw: { billing: 'Muhtasari wa bili', billingHint: 'Mpangilio wako wa sasa wa bili.', choosePlan: 'Chagua mpango wako', close: 'Funga', current: 'Wa sasa', currentAccess: 'Ufikiaji wa sasa', issued: 'Imetolewa', print: 'Chapisha', protected: 'Ufikiaji uliolindwa', protectedHint: 'Bili rahisi yenye ulinzi wazi.', receipt: 'Nambari ya risiti', selected: 'Imechaguliwa', total: 'Jumla' },
  yo: { billing: 'Àkótán ìdíyelé', billingHint: 'Ètò ìdíyelé rẹ lọwọlọwọ.', choosePlan: 'Yan ètò rẹ', close: 'Pa', current: 'Lọwọlọwọ', currentAccess: 'Ìwọlé lọwọlọwọ', issued: 'Ti gbé jáde', print: 'Tẹ̀ jáde', protected: 'Ìwọlé tó ní ààbò', protectedHint: 'Ìdíyelé tó rọrùn pẹ̀lú ààbò tó ṣe kedere.', receipt: 'Nọ́mbà rísítì', selected: 'Ti yàn', total: 'Àpapọ̀' },
  zu: { billing: 'Ukubuka ukukhokha', billingHint: 'Izilungiselelo zakho zamanje zokukhokha.', choosePlan: 'Khetha uhlelo', close: 'Vala', current: 'Okwamanje', currentAccess: 'Ukufinyelela kwamanje', issued: 'Kukhishwe', print: 'Phrinta', protected: 'Ukufinyelela okuvikelekile', protectedHint: 'Ukukhokha okulula nokuvikelwa okucacile.', receipt: 'Inombolo yerisidi', selected: 'Kukhethiwe', total: 'Ingqikithi' },
};

function formatMoney(amountMinor, currencyCode, locale = "en-NG") {
  return normalizeDollarSymbol(new Intl.NumberFormat(locale, {
    currency: currencyCode || "NGN",
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: currencyCode === "NGN" ? 0 : 2,
    style: "currency",
  }).format(Number(amountMinor || 0) / 100));
}

function formatInterval(plan) {
  const unit = plan?.intervalUnit === "year" ? "year" : "month";
  return plan?.intervalCount > 1 ? `every ${plan.intervalCount} ${unit}s` : `per ${unit}`;
}

function daysRemaining(value) {
  if (!value) return 0;
  return Math.max(0, Math.ceil((new Date(value).getTime() - Date.now()) / 86400000));
}

export default function Subscription() {
  const navigate = useNavigate();
  const { language, locale, messages } = useLocalization();
  const subscriptionCopy = SUBSCRIPTION_COPY[language] || SUBSCRIPTION_COPY.en;
  const checkout = checkoutCopy[language] || checkoutCopy.en;
  const [searchParams, setSearchParams] = useSearchParams();
  const receiptReference = searchParams.get("receipt") || "";
  const { session, updateSession } = useAuth();
	const pendingReferenceKey = `${PENDING_REFERENCE_KEY}.${session?.shopId || session?.sessionId || "unknown"}`;
	const pendingUrlKey = `${PENDING_URL_KEY}.${session?.shopId || session?.sessionId || "unknown"}`;
  const sessionRef = useRef(session);
  const autoConfirmedReference = useRef("");
  const [payload, setPayload] = useState(() => (
    session?.subscription ? { plans: session.subscription.plan ? [session.subscription.plan] : [], subscription: session.subscription } : null
  ));
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [autoRenew, setAutoRenew] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [operation, setOperation] = useState("");
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [receiptDownloadUrl, setReceiptDownloadUrl] = useState("");
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [pendingReference, setPendingReference] = useState(() => readSessionStorageValue(pendingReferenceKey));
  const [pendingUrl, setPendingUrl] = useState(() => readSessionStorageValue(pendingUrlKey));
  const [wallet, setWallet] = useState(null);

  const subscription = payload?.subscription;
  const plans = payload?.plans || [];
  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId) || plans[0];
	const currentPlan = subscription?.plan || null;
	const cycleIsActive = Boolean(subscription?.canUseApp && (subscription?.currentPeriodEndsAt || subscription?.trialEndsAt));
	const selectedIsCurrent = Boolean(currentPlan && selectedPlan?.id === currentPlan.id);
	const downgradeLocked = Boolean(cycleIsActive && currentPlan && selectedPlan && selectedPlan.sortOrder < currentPlan.sortOrder);
  const trialDays = subscription?.trialDurationDays || 3;
  const hasAccess = Boolean(subscription?.canUseApp);
  const canManageBilling = hasPermission(session, "billing");
  const supportsSquad = subscription?.availableProviders?.includes("squad");
  const selectedPriceMinor = selectedPlan ? billingCyclePrice(selectedPlan.priceMinor, billingCycle) : 0;
  const walletCoversSelection = Boolean(wallet && selectedPlan && wallet.currencyCode === selectedPlan.currencyCode && Number(wallet.availableMinor) >= selectedPriceMinor);
  useEffect(() => {
    if (subscription?.billingCycle) setBillingCycle(subscription.billingCycle);
  }, [subscription?.billingCycle]);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const updateSubscriptionSession = useCallback((nextPayload) => {
    setPayload(nextPayload);
    updateSession({
      ...sessionRef.current,
      subscription: nextPayload.subscription,
    });
  }, [updateSession]);

  const refresh = useCallback(async () => {
    if (!session?.sessionId) return;
    setError("");
    try {
      const nextPayload = await fetchSubscriptionStatus(session.sessionId);
      updateSubscriptionSession(nextPayload);
      setSelectedPlanId((current) => chooseSubscriptionPlanId(
        nextPayload.plans,
        nextPayload.subscription,
        current,
      ));
    } catch (refreshError) {
      setError(refreshError instanceof Error ? refreshError.message : "Could not load your subscription.");
    } finally {
      setIsLoading(false);
    }
  }, [session?.sessionId, updateSubscriptionSession]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!session?.sessionId) return;
    void fetchBillingWallet(session.sessionId).then((value) => setWallet(value?.wallet || null)).catch(() => undefined);
  }, [session?.sessionId]);

  const verifyPayment = useCallback(async (reference) => {
    if (!reference || !session?.sessionId || operation) return;
    setOperation("confirm");
    setError("");
    try {
      const nextPayload = await confirmSubscriptionCheckout(session.sessionId, reference);
      removeSessionStorageValue(pendingReferenceKey);
      removeSessionStorageValue(`${PENDING_URL_KEY}.${session?.shopId || session?.sessionId || "unknown"}`);
      setPendingReference("");
      setPendingUrl("");
      updateSubscriptionSession(nextPayload);
      const walletPayload = await fetchBillingWallet(session.sessionId).catch(() => null);
      if (walletPayload?.wallet) setWallet(walletPayload.wallet);
      setSelectedPlanId(chooseSubscriptionPlanId(
        nextPayload.plans,
        nextPayload.subscription,
      ));
      setSearchParams({}, { replace: true });
      toast.success("Payment verified and your workspace is active");
    } catch (confirmError) {
      setError(confirmError instanceof Error ? confirmError.message : "Payment is not confirmed yet.");
    } finally {
      setOperation("");
    }
  }, [operation, pendingReferenceKey, session?.sessionId, session?.shopId, setSearchParams, updateSubscriptionSession]);

  useEffect(() => {
    const reference = resolveSubscriptionPaymentReference(
      searchParams,
      readSessionStorageValue(pendingReferenceKey),
    );
    if (reference) {
      setPendingReference(reference);
    }
  }, [pendingReferenceKey, searchParams]);

  useEffect(() => {
    const reference = receiptReference;
    if (!reference || !session?.sessionId) {
      setReceipt(null);
      setReceiptDownloadUrl("");
      return;
    }
    setReceipt(null);
    setReceiptDownloadUrl("");
    setReceiptLoading(true);
    fetchSubscriptionPaymentReceipt(session.sessionId, reference)
      .then((result) => {
        setReceipt(result.receipt);
        setReceiptDownloadUrl(result.receiptDownloadUrl || "");
      })
      .catch((value) => setError(value instanceof Error ? value.message : "Could not load payment receipt."))
      .finally(() => setReceiptLoading(false));
  }, [receiptReference, session?.sessionId]);

  useEffect(() => {
    if (isLoading || !pendingReference || !session?.sessionId || operation || autoConfirmedReference.current === pendingReference) return;
    autoConfirmedReference.current = pendingReference;
    void verifyPayment(pendingReference);
  }, [isLoading, operation, pendingReference, session?.sessionId, verifyPayment]);

  async function startCheckout() {
	if (!session?.sessionId || !selectedPlan?.id || operation || downgradeLocked) return;
    setOperation("checkout");
    setError("");
    try {
      const result = await initializeSubscriptionCheckout(session.sessionId, {
        autoRenew,
        billingCycle,
        planId: selectedPlan.id,
        returnUrl: `${window.location.origin}/subscription`,
      });
      if (result.checkout?.settled) {
        const [nextPayload, walletPayload] = await Promise.all([fetchSubscriptionStatus(session.sessionId), fetchBillingWallet(session.sessionId)]);
        setWallet(walletPayload?.wallet || null);
        updateSubscriptionSession(nextPayload);
        toast.success("Subscription paid from your Shopbook Wallet");
        setOperation("");
        return;
      }
      writeSessionStorageValue(pendingReferenceKey, result.checkout.reference);
      writeSessionStorageValue(pendingUrlKey, result.checkout.checkoutUrl);
      setPendingReference(result.checkout.reference);
      setPendingUrl(result.checkout.checkoutUrl);
      window.location.assign(result.checkout.checkoutUrl);
    } catch (checkoutError) {
      setError(checkoutError instanceof Error ? checkoutError.message : "Could not open secure checkout.");
      setOperation("");
    }
  }

  const statusCopy = useMemo(() => {
    if (subscription?.accessStatus === "trial") {
      return {
        label: `${daysRemaining(subscription.trialEndsAt)} days remaining`,
        title: "Your free trial is active",
      };
    }
    if (subscription?.accessStatus === "active") {
      return { label: "Workspace unlocked", title: "Your subscription is active" };
    }
    return { label: "Choose how to continue", title: "Unlock your Shopbook workspace" };
  }, [subscription]);

  if (isLoading) {
    return <SubscriptionSkeleton />;
  }

  return (
    <PageContainer className="mx-auto max-w-[1480px]">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary/70">
            Billing &amp; access
          </div>
          <h1 className="mt-1.5 text-3xl font-semibold tracking-[-0.035em]">{messages.layout.subscription}</h1>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Manage workspace access, plans, and secure billing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={subscription?.accessStatus} label={statusCopy.label} />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={operation === "refresh"}
            onClick={() => {
              setOperation("refresh");
              refresh().finally(() => setOperation(""));
            }}
            className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground"
            aria-label={`${messages.layout.refreshWorkspace}: ${messages.layout.subscription}`}
          >
            <RefreshCw className={`h-4 w-4 ${operation === "refresh" ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {error ? (
        <div className="mb-5 flex items-center justify-between gap-3 rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive" role="alert">
          <span>{error}</span>
          <Button
            variant="ghost"
            size="sm"
            disabled={Boolean(operation)}
            onClick={() => {
              if (pendingReference) {
                void verifyPayment(pendingReference);
              } else {
                void refresh();
              }
            }}
          >
            {pendingReference ? "Verify payment" : "Retry"}
          </Button>
        </div>
      ) : null}

      {pendingReference ? (
        <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-950">
          <div className="text-sm font-semibold">{checkout.interrupted}</div>
          <p className="mt-1 text-xs leading-5 text-amber-900/75">{checkout.interruptedBody}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={Boolean(operation)} onClick={() => void verifyPayment(pendingReference)}>{checkout.verify}</Button>
            {pendingUrl ? <Button size="sm" onClick={() => { void recordSubscriptionCheckoutClientState(session.sessionId, pendingReference, "resumed").catch(() => undefined); window.location.assign(pendingUrl); }}>{checkout.resume}</Button> : null}
            <Button size="sm" variant="ghost" onClick={() => { void recordSubscriptionCheckoutClientState(session.sessionId, pendingReference, "abandoned").catch(() => undefined); removeSessionStorageValue(pendingReferenceKey); removeSessionStorageValue(pendingUrlKey); setPendingReference(""); setPendingUrl(""); }}>{checkout.cancel}</Button>
          </div>
        </div>
      ) : null}

      {(receipt || receiptLoading) ? (
        <Card className="mb-5 rounded-2xl border-primary/15 bg-primary/[0.035] shadow-sm">
          <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
                {receiptLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <BadgeCheck className="h-5 w-5" />}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-foreground">
                  {receiptLoading ? "Loading official receipt" : receipt?.receiptType === "free_trial" ? "Free trial receipt ready" : "Payment receipt ready"}
                </div>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  {receiptLoading
                    ? "Shopbook is retrieving the official receipt."
                    : `Receipt ${receipt?.receiptNumber || ""} is stored by Shopbook as the official record.`}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {receiptDownloadUrl ? (
                <Button asChild size="sm" className="rounded-xl">
                  <a href={receiptDownloadUrl} target="_blank" rel="noreferrer">
                    <Download className="h-4 w-4" /> Download official receipt
                  </a>
                </Button>
              ) : null}
              <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => {
                setReceipt(null);
                setReceiptDownloadUrl("");
                const next = new URLSearchParams(searchParams);
                next.delete("receipt");
                setSearchParams(next, { replace: true });
              }}>{subscriptionCopy.close}</Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1.45fr)_380px]">
        <div className="space-y-5">
          <CurrentPlanCard
            copy={subscriptionCopy}
            currentPlan={currentPlan}
            locale={locale}
            statusCopy={statusCopy}
            subscription={subscription}
          />

          <Card className="rounded-2xl border-border/70 shadow-[0_18px_55px_-48px_rgba(15,23,42,0.6)]">
            <CardHeader className="p-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <CardTitle className="text-lg tracking-[-0.025em]">{subscriptionCopy.choosePlan}</CardTitle>
                  <CardDescription className="mt-1 text-xs">
                    Select the capacity that fits your shop today.
                  </CardDescription>
                </div>
                <Badge variant="outline" className="w-fit border-border/70 bg-muted/45 text-[10px] text-muted-foreground">
                  {subscription?.billingCountryCode === "NG" ? "Nigeria · NGN" : "International · USD"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="px-5 pb-5 sm:px-6 sm:pb-6">
              <BillingPeriodSelector
                autoRenew={autoRenew}
                autoRenewBusy={Boolean(operation)}
                className="mb-4"
                onAutoRenewChange={setAutoRenew}
                onChange={setBillingCycle}
                value={billingCycle}
              />

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {plans.map((plan) => (
                  <PlanOption
                    billingCycle={billingCycle}
                    key={`${plan.id}-${plan.currencyCode}`}
                    locale={locale}
                    plan={plan}
                    selected={selectedPlan?.id === plan.id}
                    selectedLabel={subscriptionCopy.selected}
                    onSelect={() => setSelectedPlanId(plan.id)}
                  />
                ))}
              </div>

              <Separator className="my-5 bg-border/60" />

              {canManageBilling ? (
                <div>
                  <Button
                    className="h-11 w-full rounded-xl text-[12px] shadow-sm"
                    disabled={!selectedPlan || (!walletCoversSelection && !supportsSquad) || Boolean(operation) || downgradeLocked}
                    onClick={() => void startCheckout()}
                  >
                    {operation === "checkout" || operation === "confirm" ? <Loader2 className="h-4 w-4 animate-spin" /> : <LockKeyhole className="h-4 w-4" />}
					{downgradeLocked ? "Available after current period" : selectedPlan
                      ? `${walletCoversSelection ? "Pay from wallet" : "Continue"} · ${formatMoney(billingCyclePrice(selectedPlan.priceMinor, billingCycle), selectedPlan.currencyCode, locale)}`
                      : "Continue to secure checkout"}
                    <ArrowRight className="ml-auto h-4 w-4 opacity-70" />
                  </Button>
                </div>
              ) : (
                <div className="rounded-xl border border-primary/10 bg-primary/[0.045] px-4 py-3 text-center text-xs font-medium text-primary">
                  Ask the shop owner to choose a plan or manage billing.
                </div>
              )}

              <div className="mt-4 flex items-start gap-2.5 text-[11px] leading-5 text-muted-foreground">
                <Clock3 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                <p>
                  Trials last exactly {trialDays} days with no automatic charge. Paid access starts only after Shopbook verifies the payment.
                </p>
              </div>
              <div className="mt-3 flex items-start gap-2.5 rounded-xl bg-primary/[0.045] p-3 text-[11px] leading-5 text-muted-foreground">
                <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                <p>{walletCoversSelection ? `Your ${formatMoney(wallet.availableMinor, wallet.currencyCode, locale)} wallet balance covers this payment. Squad will not open.` : checkout.walletFirst}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <aside className="space-y-5">
          <BillingOverview copy={subscriptionCopy} subscription={subscription} currentPlan={currentPlan} supportsSquad={supportsSquad} />
          <AccessCard copy={subscriptionCopy} />
          {hasAccess ? (
            <Button
              variant="outline"
              className="h-10 w-full rounded-xl border-primary/15 bg-card text-[12px] text-primary shadow-none"
              onClick={() => navigate("/dashboard")}
            >
              <BadgeCheck className="h-4 w-4" />
              Back to dashboard
            </Button>
          ) : null}
        </aside>
      </div>
    </PageContainer>
  );
}

function CurrentPlanCard({ copy, currentPlan, locale, statusCopy, subscription }) {
  return (
    <Card className="relative overflow-hidden rounded-2xl border-primary/10 bg-card shadow-[0_24px_70px_-48px_rgba(6,78,59,0.5)]">
      <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-primary/[0.055] blur-2xl" />
      <div className="pointer-events-none absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-primary via-emerald-400 to-amber-300" />
      <CardContent className="relative p-5 sm:p-6">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-xl">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-primary/10 bg-primary/[0.07] text-primary">
                <Crown className="h-4 w-4" />
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary/70">{copy.currentAccess}</span>
            </div>
            <h2 className="mt-4 text-xl font-semibold tracking-[-0.025em]">{statusCopy.title}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Sales, inventory, staff, and insights stay together in your secure workspace.
            </p>
          </div>

          <div className="w-full rounded-xl border border-primary/10 bg-primary/[0.035] p-4 sm:max-w-[290px]">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-semibold">{currentPlan?.name || "No plan selected"}</span>
              <StatusBadge status={subscription?.accessStatus} label={statusCopy.label} />
            </div>
            <div className="mt-4 flex items-end gap-2">
              <span className="font-mono text-2xl font-semibold tracking-[-0.04em]">
                {currentPlan ? formatMoney(currentPlan.priceMinor, currentPlan.currencyCode, locale) : "—"}
              </span>
              <span className="pb-1 text-[10px] text-muted-foreground">
                {currentPlan ? formatInterval(currentPlan) : ""}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PlanOption({ billingCycle, locale, onSelect, plan, selected, selectedLabel }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`group relative min-h-[154px] rounded-xl border p-4 text-left transition-all ${
        selected
          ? "border-primary/45 bg-primary/[0.055] shadow-[0_12px_30px_-24px_hsl(var(--primary))] ring-1 ring-primary/10"
          : "border-border/70 bg-background/45 hover:-translate-y-0.5 hover:border-primary/20 hover:bg-primary/[0.025]"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className={`flex h-6 w-6 items-center justify-center rounded-full border ${selected ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-transparent"}`}>
          <Check className="h-3.5 w-3.5" />
        </div>
        {selected ? <Badge className="h-5 rounded-full px-2 text-[9px]">{selectedLabel}</Badge> : null}
      </div>
      <div className="mt-4 text-sm font-semibold">{plan.name}</div>
      <div className="mt-1 line-clamp-2 min-h-8 text-[11px] leading-4 text-muted-foreground">{plan.description}</div>
      <div className="mt-2 text-[10px] font-medium text-primary/75">
        {Number(plan.includedCredits || 0).toLocaleString()} Credits / month
      </div>
      <div className="mt-3 font-mono text-lg font-semibold tracking-[-0.03em] text-primary">
        {formatMoney(billingCyclePrice(plan.priceMinor, billingCycle), plan.currencyCode, locale)}
        <span className="ml-1 font-sans text-[9px] font-normal tracking-normal text-muted-foreground">{billingCycleInterval(billingCycle)}</span>
      </div>
    </button>
  );
}

function BillingOverview({ copy, currentPlan, subscription, supportsSquad }) {
  return (
    <Card className="rounded-2xl border-border/70 shadow-[0_18px_55px_-48px_rgba(15,23,42,0.6)]">
      <CardHeader className="p-5 pb-3">
        <CardTitle className="text-base tracking-[-0.02em]">{copy.billing}</CardTitle>
        <CardDescription className="text-xs">{copy.billingHint}</CardDescription>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        <OverviewRow icon={Crown} label="Plan" value={currentPlan?.name || "Not selected"} />
        <Separator className="my-3 bg-border/60" />
        <OverviewRow icon={Store} label="Billing region" value={subscription?.billingCountryCode === "NG" ? "Nigeria · NGN" : "International · USD"} />
        <Separator className="my-3 bg-border/60" />
        <OverviewRow icon={CreditCard} label="Payment" value={supportsSquad ? "Squad" : "Unavailable"} />
      </CardContent>
    </Card>
  );
}

function OverviewRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/[0.07] text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1 text-xs text-muted-foreground">{label}</div>
      <div className="max-w-[160px] truncate text-right text-xs font-semibold text-foreground">{value}</div>
    </div>
  );
}

function AccessCard({ copy }) {
  const features = [
    { icon: ShieldCheck, text: "Server-verified billing" },
    { icon: Sparkles, text: "Core operations included" },
    { icon: LockKeyhole, text: "No automatic trial charge" },
  ];
  return (
    <Card className="rounded-2xl border-border/70 bg-muted/20 shadow-none">
      <CardHeader className="p-5 pb-3">
        <CardTitle className="text-base tracking-[-0.02em]">{copy.protected}</CardTitle>
        <CardDescription className="text-xs">{copy.protectedHint}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 px-5 pb-5">
        {features.map(({ icon: Icon, text }) => (
          <div key={text} className="flex items-center gap-3 text-xs text-muted-foreground">
            <Check className="h-3.5 w-3.5 text-success" />
            <span className="flex-1">{text}</span>
            <Icon className="h-3.5 w-3.5 text-primary/60" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ label, status }) {
  const tone = status === "active"
    ? "border-success/15 bg-success/10 text-success"
    : status === "trial"
      ? "border-warning/15 bg-warning/10 text-warning"
      : "border-border bg-muted/55 text-muted-foreground";
  return (
    <Badge variant="outline" className={`h-6 rounded-full px-2.5 text-[9px] font-semibold ${tone}`}>
      {label}
    </Badge>
  );
}

function SubscriptionSkeleton() {
  return (
    <PageContainer className="mx-auto max-w-[1480px]">
      <div className="space-y-5">
        <Skeleton className="h-16 w-full max-w-md rounded-xl" />
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.45fr)_380px]">
          <div className="space-y-5">
            <Skeleton className="h-56 rounded-2xl" />
            <Skeleton className="h-96 rounded-2xl" />
          </div>
          <div className="space-y-5">
            <Skeleton className="h-56 rounded-2xl" />
            <Skeleton className="h-48 rounded-2xl" />
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
